import React, { useMemo, useState } from 'react';
import { Modal, Form } from 'react-bootstrap';
import { FlagIcon } from 'react-flag-kit';
import { RiCloseCircleFill, RiWhatsappFill } from 'react-icons/ri';
import { FaLink } from 'react-icons/fa6';
import { IoMdDownload } from 'react-icons/io';
import { MdContentCopy, MdCompareArrows } from 'react-icons/md';
import { BsFileEarmarkPdf } from 'react-icons/bs';
import './PktCompareComp.css';

/**
 * PktCompareComp
 * ----------------------------------------------------------------------
 * Fully self-contained, dynamic "Compare Packets" modal.
 * Renders one comparison card per selected packet (any count, N cards),
 * with an optional "highlight differences" mode.
 *
 * USAGE (from any screen / component):
 *   const [showCompare, setShowCompare] = useState(false);
 *
 *   <PktCompareComp
 *       show={showCompare}
 *       onHide={() => setShowCompare(false)}
 *       data={Selectedrow?.filter(x => x.SELECTED)}   // <-- selected row objects
 *   />
 *
 * NOTE ON FIELD NAMES:
 * Every key below matches the actual row object shape used across the app
 * (the same object shape as objDs / Selectedrow in DataListComp). Every
 * field visible in the reference comparison design is mapped to a real
 * column, and is ALWAYS shown (falls back to "-" when blank) — only
 * columns that don't exist at all in the data source stay hidden:
 *   Identity      : PKTID, MFGID, LAB, CERTNO, COU_NAME/COU_CODE, STNSTATUS
 *   Specs         : S_NAME, I_CARAT, C_NAME, Q_NAME, CT_NAME, PO_NAME,
 *                   SY_NAME, FL_NAME
 *   Supplier note : MBMCOMMENTS (top comment line)
 *   Pricing       : ORATE (Rap), RATE (Price/ct), DISC, AMT
 *   Parameters    : TABLE_PER, CRWANGLE, DIAMONDTYPE, TOTDEPTH_PER,
 *                   CRWHEIGHT_PER, RATIO, PAVANGLE, GIRDLE_NAME (Girdle
 *                   Desc.), MEASUREMENTS (L-W-H), PAVDEPTH_PER,
 *                   INSCRIPTION, LHALF (Lower Half%), GIRDLEPER (Girdle %),
 *                   REMARK (2nd "Supplier Comment" slot), STAR_PER
 *                   (Star Length%)
 *   Notes         : KEYTOSYMB (Key to Symbols), REPORTCOMMENT (Report
 *                   Comment — no column yet, always shows "-")
 *   Inclusions    : BLACKTABLEINCNAME (Table Black), BLACKSIDEINCNAME
 *                   (Side Black), OPENTABLEINCNAME (Table Open),
 *                   OPENSIDEINCNAME (Side Open), WHITETABLEINCNAME (Table
 *                   Inclusion), WHITESIDEINCNAME (Side Inclusion),
 *                   OPENPAVINCNAME (Pavilion Open) — all inclusion flags
 *                   always render, defaulting to "Non" when there's no
 *                   value, same as a real grading report.
 * Fields with no column at all yet (Culet, Girdle Open, Extra Facet, Red
 * Spot) are kept in the config as CULET / GIRDLEOPEN / EXTRAFACET /
 * REDSPOT so they appear automatically the day those columns exist.
 * ----------------------------------------------------------------------
 */

const SPEC_FIELDS = [
    { label: 'Shape', key: 'S_NAME' },
    { label: 'Carat', key: 'I_CARAT', suffix: ' ct', decimals: 2 },
    { label: 'Color', key: 'C_NAME' },
    { label: 'Clarity', key: 'Q_NAME' },
    { label: 'Cut', key: 'CT_NAME' },
    { label: 'Polish', key: 'PO_NAME' },
    { label: 'Sym', key: 'SY_NAME' },
    { label: 'Floro', key: 'FL_NAME' },
];

const PRICE_FIELDS = [
    { label: 'Rap', key: 'ORATE', prefix: '$', decimals: 0 },
    { label: 'Disc%', key: 'DISC', suffix: '%', decimals: 2, invertGood: true },
    { label: 'Price/ct', key: 'RATE', prefix: '$', decimals: 2 },
    { label: 'Amount', key: 'AMT', prefix: '$', decimals: 2, emphasis: true },
];

const PARAM_GRIDOld = [
    [{ label: 'Table %', key: 'TABLE_PER', decimals: 2 }, { label: 'Cr. Angle', key: 'CRWANGLE', decimals: 2 }, { label: 'Diam Type', key: 'DIAMONDTYPE' }],
    [{ label: 'Total Depth', key: 'TOTDEPTH_PER', decimals: 2 }, { label: 'Cr. Height', key: 'CRWHEIGHT_PER', decimals: 2 }, { label: 'Culet', key: 'CULET' }],
    [{ label: 'Ratio', key: 'RATIO', decimals: 2 }, { label: 'Pav. Angle', key: 'PAVANGLE', decimals: 2 }, { label: 'Girdle Desc.', key: 'GIRDLE_NAME' }],
    [{ label: 'L-W-H', key: 'MEASUREMENTS' }, { label: 'Pav. Height', key: 'PAVDEPTH_PER', decimals: 2 }, { label: 'Inscription', key: 'INSCRIPTION' }],
    [{ label: 'Lower Half%', key: 'LHALF', decimals: 2 }, { label: 'Girdle %', key: 'GIRDLEPER', decimals: 2 }, { label: 'Supplier Comment', key: 'REMARK' }],
    [{ label: 'Star Length%', key: 'STAR_PER', decimals: 2 }],
];

const PARAM_GRID = [
    [{ label: 'Table %', key: 'TABLE_PER', decimals: 2 }, { label: 'Cr. Angle', key: 'CRWANGLE', decimals: 2 }, { label: 'Cr. Height', key: 'CRWHEIGHT_PER', decimals: 2 }, { label: 'Diam Type', key: 'DIAMONDTYPE' }],
    [{ label: 'Total Depth', key: 'TOTDEPTH_PER', decimals: 2 }, { label: 'Pav. Angle', key: 'PAVANGLE', decimals: 2 }, { label: 'Culet', key: 'CULET' }, { label: 'Ratio', key: 'RATIO', decimals: 2 },],
    [{ label: 'Girdle Desc.', key: 'GIRDLE_NAME' }, { label: 'Girdle %', key: 'GIRDLEPER', decimals: 2 }, { label: 'Lower Half%', key: 'LHALF', decimals: 2 }, { label: 'Pav. Height', key: 'PAVDEPTH_PER', decimals: 2 },],
    [{ label: 'L-W-H', key: 'MEASUREMENTS' }, { label: 'Inscription', key: 'INSCRIPTION' }, { label: 'Supplier Comment', key: 'REMARK' }, { label: 'Star Length%', key: 'STAR_PER', decimals: 2 }],

];

const INCLUSION_FLAGS = [
    { label: 'Table Black', key: 'BLACKTABLEINCNAME' },
    { label: 'Side Black', key: 'BLACKSIDEINCNAME' },
    { label: 'Table Open', key: 'OPENTABLEINCNAME' },
    { label: 'Side Open', key: 'OPENSIDEINCNAME' },
    { label: 'Table Inclusion', key: 'WHITETABLEINCNAME' },
    { label: 'Side Inclusion', key: 'WHITESIDEINCNAME' },
    { label: 'Girdle Open', key: 'GIRDLEOPEN' },
    { label: 'Pavilion Open', key: 'OPENPAVINCNAME' },
    { label: 'Extra Facet', key: 'EXTRAFACET' },
    { label: 'Red Spot', key: 'REDSPOT' },
];

const fmt = (row, field) => {
    const { key, prefix = '', suffix = '', decimals } = field;
    const hasColumn = row && Object.prototype.hasOwnProperty.call(row, key);
    if (!hasColumn) return null; // column doesn't exist in this data source at all — stay hidden
    const val = row[key];
    if (val === undefined || val === null || val === '') return '-'; // column exists, just empty for this stone
    if (decimals !== undefined && !isNaN(Number(val))) {
        return `${prefix}${Number(val).toFixed(decimals)}${suffix}`;
    }
    return `${prefix}${val}${suffix}`;
};

const formatPrintData = (row) => {
    return `PKT ID : ${row.PKTID} ${row.MFGID || ''} ${row.LAB || ''} ${row.CERTNO || ''}
${(row.S_NAME || '').toUpperCase()} ${row.I_CARAT || ''} ${row.C_NAME || ''} ${row.Q_NAME || ''} ${row.CT_NAME || ''} ${row.PO_NAME || ''} ${row.SY_NAME || ''} ${row.FL_NAME || ''}
L-W-H : ${row.MEASUREMENTS || '-'}
Table : ${row.TABLE_PER || '-'} , Depth : ${row.TOTDEPTH_PER || '-'} , Ratio : ${row.RATIO || '-'}
Girdle : ${row.GIRDLE_NAME || '-'} , Inscription : ${row.INSCRIPTION || '-'}
Rap : $${row.ORATE || 0} , Disc : ${row.DISC || 0}% , Price/ct : $${row.RATE || 0} , Amount : $${row.AMT || 0}
${row.MBMCOMMENTS || row.REMARK ? `Comment : ${row.MBMCOMMENTS || row.REMARK}\n` : ''}Video : ${row.VIDEOLINK1 || '-'}
Cert PDF : ${row.PDFLINK || row.REPORTLINK || '-'}
---------------------`;
};

function PktCompareComp({ show, onHide, data = [], onRemove }) {
    const [highlightDiff, setHighlightDiff] = useState(true);
    const [imgError, setImgError] = useState({});

    const rows = Array.isArray(data) ? data.filter(Boolean) : [];


    // Pre-compute, for every field across every section, whether values differ
    // across the compared packets — used to visually flag differences.
    const diffKeys = useMemo(() => {
        const allFieldKeys = [
            ...SPEC_FIELDS.map(f => f.key),
            ...PRICE_FIELDS.map(f => f.key),
            ...PARAM_GRID.flat().map(f => f.key),
        ];
        const result = {};
        allFieldKeys.forEach((key) => {
            const values = rows.map(r => (r?.[key] ?? '')).map(v => String(v).trim());
            const unique = new Set(values.filter(v => v !== ''));
            result[key] = unique.size > 1;
        });
        return result;
    }, [rows]);

    const handleImgError = (idx) => setImgError(prev => ({ ...prev, [idx]: true }));

    const copyOne = (row) => {
        navigator.clipboard.writeText(formatPrintData(row)).catch(() => { });
    };

    const copyLink = (link) => {
        if (!link) return;
        navigator.clipboard.writeText(link).catch(() => { });
    };

    const copyText = (text) => {
        if (!text) return;
        navigator.clipboard.writeText(String(text)).catch(() => { });
    };

    const downloadFile = async (url, filename) => {
        if (!url) return;
        try {
            const response = await fetch(url);
            if (!response.ok) throw new Error('download failed');
            const blob = await response.blob();
            const objUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = objUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(objUrl);
            document.body.removeChild(a);
        } catch (e) {
            // silent fail — same behaviour pattern used elsewhere in the app
        }
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            dialogClassName="pkt-compare-dialog"
            fullscreen="lg-down"
            centered
        >
            <Modal.Header closeButton className="pkt-compare-header">
                <div className='d-flex justify-content-center align-items-center'>
                    <div className="d-flex align-items-center gap-2">
                        <MdCompareArrows size={22} className="me-2" />
                        <div>
                            <Modal.Title className="pkt-compare-title">Compare Packets</Modal.Title>
                            <div className="pkt-compare-subtitle">
                                {rows.length} {rows.length === 1 ? 'packet' : 'packets'} selected
                            </div>
                        </div>
                    </div>

                    <div className='ms-3'>
                        {rows.length > 1 && (
                            <Form.Check
                                type="switch"
                                id="pkt-compare-diff-switch"
                                label="Highlight differences"
                                checked={highlightDiff}
                                onChange={() => setHighlightDiff(v => !v)}
                                className="pkt-compare-diff-toggle"
                            />
                        )}
                    </div>
                </div>
            </Modal.Header>

            <Modal.Body className="pkt-compare-body">
                {rows.length === 0 ? (
                    <div className="pkt-compare-empty">
                        <MdCompareArrows size={40} className="mb-2 opacity-50" />
                        <p className="m-0 fw-semibold">No packets selected</p>
                        <p className="m-0 text-muted small">
                            Select stones from the grid using the checkboxes, then click <b>Compare Pkt</b> again.
                        </p>
                    </div>
                ) : (
                    <div className="pkt-compare-track">
                        {rows.map((row, idx) => (
                            <div className="pkt-compare-card" key={row.PKTID ?? idx}>
                                {onRemove && (
                                    <button
                                        type="button"
                                        className="pkt-compare-remove"
                                        title="Remove from comparison"
                                        onClick={() => onRemove(row)}
                                    >
                                        <RiCloseCircleFill size={20} />
                                    </button>
                                )}


                                <div className="pkt-compare-img-wrap">
                                    <div className="pkt-compare-img-badges">
                                        <span className="badge-type">
                                            {row.DIAMONDTYPE || row.S_NAME || '-'}
                                        </span>

                                        <span className="badge-lab">
                                            {row.LAB || '-'}
                                        </span>
                                    </div>

                                    {row.VIDEOLINK1 ? (
                                        <iframe
                                            src={row.VIDEOLINK1}
                                            title="360 Viewer"
                                            className="diamond-viewer"
                                            scrolling="no"
                                            frameBorder="0"
                                            allow="autoplay"
                                            referrerPolicy="no-referrer"
                                            sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals"
                                        />
                                    ) : (
                                        <div className="pkt-compare-img-fallback">
                                            Video Not Available
                                        </div>
                                    )}
                                </div>
                                

                                {/* ---------- Identity ---------- */}
                                <div className="pkt-compare-identity">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="pkt-compare-id">Stock ID : {row.PKTID}</span>
                                            {row.MFGID && <span className="pkt-compare-mfgid">{row.MFGID}</span>}
                                            <button
                                                type="button"
                                                className="pkt-compare-copy-btn"
                                                title="Copy Packet ID"
                                                onClick={() => copyText(row.MFGID || row.PKTID)}
                                            >
                                                <MdContentCopy size={12} />
                                            </button>
                                        </div>
                                        <div className="pkt-compare-country ms-2">
                                            {row.COU_FLAG ? (
                                                <span
                                                    className="pkt-compare-country-flag"
                                                    dangerouslySetInnerHTML={{ __html: row.COU_FLAG }}
                                                />
                                            ) : row.COU_CODE ? (
                                                <FlagIcon code={row.COU_CODE} size={14} />
                                            ) : null}

                                            <span className="pkt-compare-country-name">
                                                {row.COU_NAME}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center justify-content-between mt-1">
                                        <div className="pkt-compare-cert">
                                            {row.LAB || ''} {row.CERTNO ? `• ${row.CERTNO}` : ''}
                                        </div>
                                        {row.STNSTATUS && (
                                            <span
                                                className="pkt-compare-status ms-2"
                                                style={row.STNSTATUS_COLOR ? { backgroundColor: row.STNSTATUS_COLOR } : undefined}
                                            >
                                                {row.STNSTATUS}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="pkt-compare-comment">
                                    <span className="lbl">Supplier comment</span>
                                    <span>{row.MBMCOMMENTS || '-'}</span>
                                </div>

                                {/* ---------- Primary specs ---------- */}
                                <div className="pkt-compare-spec-row">
                                    {SPEC_FIELDS.map((f) => {
                                        const display = fmt(row, f);
                                        if (display === null) return null;
                                        return (
                                            <div
                                                className={`pkt-compare-spec-chip${highlightDiff && diffKeys[f.key] ? ' is-diff' : ''}`}
                                                key={f.key}
                                            >
                                                <span className="chip-label">{f.label}</span>
                                                <span className="chip-value">{display}</span>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* ---------- Pricing ---------- */}
                                <div className="pkt-compare-price-row">
                                    {PRICE_FIELDS.map((f) => {
                                        const display = fmt(row, f);
                                        if (display === null) return null;
                                        return (
                                            <div
                                                className={`pkt-compare-price-tile${f.emphasis ? ' is-emphasis' : ''}${highlightDiff && diffKeys[f.key] ? ' is-diff' : ''}`}
                                                key={f.key}
                                            >
                                                <span className="tile-label">{f.label}</span>
                                                <span className="tile-value">{display}</span>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* ---------- Parameters ---------- */}
                                <div className="pkt-compare-params">
                                    {PARAM_GRID.map((line, li) => (
                                        <div className="pkt-compare-param-line" key={li}>
                                            {line.map((f) => {
                                                const display = fmt(row, f);
                                                if (display === null) return null;
                                                return (
                                                    <div
                                                        className={`pkt-compare-param${highlightDiff && diffKeys[f.key] ? ' is-diff' : ''}`}
                                                        key={f.key}
                                                    >
                                                        <span className="p-label">{f.label}</span>
                                                        <span className="p-value">{display}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ))}
                                </div>

                                {/* ---------- Key to symbols / report comment ---------- */}
                                <div className="pkt-compare-notes">
                                    <div><span className="lbl">Key to Symbols</span><span>{row.KEYTOSYMB || '-'}</span></div>
                                    <div><span className="lbl">Report Comment</span><span>{row.REPORTCOMMENT || '-'}</span></div>
                                </div>

                                {/* ---------- Inclusion flags ---------- */}
                                <div className="pkt-compare-flags">
                                    {INCLUSION_FLAGS.map((f) => {
                                        const val = row[f.key] || 'Non';
                                        const isNone = String(val).toLowerCase() === 'non' || String(val).toLowerCase() === 'none';
                                        return (
                                            <span
                                                key={f.key}
                                                className={`pkt-compare-flag${isNone ? ' is-none' : ' is-active'}`}
                                                title={f.label}
                                            >
                                                <span className="flag-label">{f.label}</span>
                                                <span className="flag-value">{val}</span>
                                            </span>
                                        );
                                    })}
                                </div>

                                {/* ---------- Actions ---------- */}
                                <div className="pkt-compare-actions">
                                    <button type="button" className="pkt-compare-action-btn" onClick={() => copyOne(row)}>
                                        <MdContentCopy size={15} className="me-1" /> Copy
                                    </button>
                                    {row.PHOTOLINK && (
                                        <button
                                            type="button"
                                            className="pkt-compare-action-btn"
                                            onClick={() => window.open(row.PHOTOLINK, "_blank", "noopener,noreferrer")}
                                        >
                                            <IoMdDownload size={15} className="me-1" />
                                            Image
                                        </button>
                                    )}
                                    {row.VIDEOLINK1 && (
                                        <button
                                            type="button"
                                            className="pkt-compare-action-btn"
                                            onClick={() => window.open(row.VIDEOLINK1, "_blank", "noopener,noreferrer")}
                                        >
                                            <FaLink size={13} className="me-1" />
                                            Video
                                        </button>
                                    )}
                                    {(row.REPORTLINK || row.PDFLINK) && (
                                        <button
                                            type="button"
                                            className="pkt-compare-action-btn"
                                            onClick={() => window.open(row.REPORTLINK || row.PDFLINK, '_blank')}
                                        >
                                            <BsFileEarmarkPdf size={14} className="me-1" /> Cert
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer className="pkt-compare-footer">
                <button type="button" className="btn btn-secondary btn-sm" onClick={onHide}>
                    <RiCloseCircleFill size={16} className="me-1" /> Close
                </button>
            </Modal.Footer>
        </Modal>
    );
}

export default PktCompareComp;